import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, TextField, Stack, Select, MenuItem, FormControl, InputLabel, Button, Radio, RadioGroup, FormControlLabel } from '@mui/material';
import CryptoJS from 'crypto-js';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import SEO, { createSoftwareAppSchema } from '../../components/SEO';
import ToolPageContainer from '../../components/ToolPageContainer';

function HMACGenerator() {
  const [message, setMessage] = useState('Hello World');
  const [secret, setSecret] = useState('secret-key');
  const [algorithm, setAlgorithm] = useState('HmacSHA256');
  const [outputFormat, setOutputFormat] = useState('hex');
  const [hash, setHash] = useState('');

  useEffect(() => {
    try {
      if (!message && !secret) {
        setHash('');
        return;
      }
      
      const hmacResult = CryptoJS[algorithm](message, secret);
      
      if (outputFormat === 'hex') {
        setHash(hmacResult.toString(CryptoJS.enc.Hex));
      } else if (outputFormat === 'base64') {
        setHash(hmacResult.toString(CryptoJS.enc.Base64));
      }
    } catch (e) {
      setHash('Error generating HMAC.');
    }
  }, [message, secret, algorithm, outputFormat]);

  const handleCopy = () => {
    if (hash && hash !== 'Error generating HMAC.') {
      navigator.clipboard.writeText(hash);
    }
  };

  return (
    <>
      <SEO
        title="HMAC Generator | Create HMAC Signatures | UmbrellaCo"
        description="Generate HMAC signatures online using SHA256, SHA512, MD5, and other algorithms. Free developer tool for API authentication and webhook verification."
        keywords="hmac generator, create hmac signature, hmac sha256, hmac md5, webhook signature generator"
        url="/dev-tools/hmac-generator"
        jsonLd={createSoftwareAppSchema(
          'HMAC Generator',
          'Generate secure HMAC signatures using various cryptographic hash functions.',
          'https://umbrellaco.in/dev-tools/hmac-generator',
          ['HMAC Generation', 'SHA256', 'MD5', 'API Security']
        )}
      />
      <ToolPageContainer
        title="HMAC Generator"
        description="Generate Hash-based Message Authentication Codes (HMAC) to secure API requests or verify webhooks."
        disablePadding
      >
      <Box sx={{ p: 3, display: 'flex', gap: 3, flexGrow: 1, minHeight: 0 }}>
        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', gap: 3, overflowY: 'auto' }}>
          <TextField
            label="Message (String)"
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            variant="outlined"
            fullWidth
          />
          
          <TextField
            label="Secret Key"
            value={secret}
            onChange={(e) => setSecret(e.target.value)}
            variant="outlined"
            fullWidth
          />

          <Stack direction="row" spacing={3}>
            <FormControl flex={1} sx={{ minWidth: 200 }}>
              <InputLabel>Algorithm</InputLabel>
              <Select
                value={algorithm}
                label="Algorithm"
                onChange={(e) => setAlgorithm(e.target.value)}
              >
                <MenuItem value="HmacMD5">HMAC-MD5</MenuItem>
                <MenuItem value="HmacSHA1">HMAC-SHA1</MenuItem>
                <MenuItem value="HmacSHA256">HMAC-SHA256</MenuItem>
                <MenuItem value="HmacSHA384">HMAC-SHA384</MenuItem>
                <MenuItem value="HmacSHA512">HMAC-SHA512</MenuItem>
              </Select>
            </FormControl>

            <FormControl component="fieldset">
              <Typography variant="caption" color="text.secondary">Output Format</Typography>
              <RadioGroup
                row
                value={outputFormat}
                onChange={(e) => setOutputFormat(e.target.value)}
              >
                <FormControlLabel value="hex" control={<Radio />} label="Hex" />
                <FormControlLabel value="base64" control={<Radio />} label="Base64" />
              </RadioGroup>
            </FormControl>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, flex: 1, display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="h6">Generated HMAC</Typography>
            <Button variant="contained" onClick={handleCopy} startIcon={<ContentCopyIcon />}>Copy</Button>
          </Stack>
          <Box sx={{ p: 3, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider', wordBreak: 'break-all', fontFamily: 'monospace', fontSize: '1.2rem', flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {hash ? (
              <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '1.2rem' }}>{hash}</Typography>
            ) : (
              <Typography color="text.secondary">Provide a message and secret key.</Typography>
            )}
          </Box>
        </Paper>
      </Box>
      </ToolPageContainer>
    </>
  );
}

export default HMACGenerator;
